/**
 * LayoutLoadingState - Observer компонент для відображення стану завантаження
 * 
 * Цей компонент обгорнутий в observer і реагує на зміни в LayoutLoadingStore.
 * Створює layoutContext ТІЛЬКИ після завантаження даних з loadData.
 */

import { observer } from 'mobx-react-lite'
import React, { useMemo } from 'react'
import { LayoutTemplateConfig } from '../../Commands/Loaders/LayoutTemplateLoader'
import { setKernelLayoutStore } from '../../Services/KernelRegistry'
import { createLayoutContext, LayoutContextConfig, LayoutContextProvider } from './LayoutContextProvider'
import type { LayoutLoadingStore } from './LayoutLoadingStore'
import type { KernelLike } from '../../Services/KernelRegistry'

interface LayoutLoadingStateProps {
    store: LayoutLoadingStore
    routeTemplate: string
    kernel: KernelLike
    layoutWrapper: React.ComponentType<{ children: React.ReactNode }>
    routeMetadata: any
    env: any
    children: React.ReactNode
}

export const LayoutLoadingState: React.FC<LayoutLoadingStateProps> = observer(
    ({ store, routeTemplate, kernel, layoutWrapper, routeMetadata, env, children }) => {
        // Завжди викликаємо useMemo для дотримання правил хуків
        // Мемоізуємо layoutContext - він змінюється тільки коли змінюється template або loadedData
        const layoutContext = useMemo(() => {
            // Якщо дані ще завантажуються, не створюємо контекст
            if (store.isLoadingData || store.loadDataError) {
                return null
            }
            
            const layoutContextConfig: LayoutContextConfig = {
                kernel,
                template: routeTemplate,
                templateWrapper: layoutWrapper,
                children,
                ...(routeMetadata.accessFn && { accessFn: routeMetadata.accessFn }),
                ...(routeMetadata.templateError403 && { templateError403: routeMetadata.templateError403 }),
                // Ініціалізуємо storages з loadedData одразу
                storages: {
                    loadedData: store.loadedData,
                },
                debug: {
                    id: `layout-${routeTemplate}`,
                    name: `DefaultLayout`,
                    type: 'template' as const,
                    path: '/Core/Layouts/DefaultLayout/Layout.tsx',
                    enabled: env.DEV,
                },
            }

            const context = createLayoutContext(layoutContextConfig)
            
            // Зберігаємо layoutContext в глобальному реєстрі
            setKernelLayoutStore(context)
            
            return context
        }, [kernel, routeTemplate, layoutWrapper, routeMetadata, env.DEV, store.loadedData, store.isLoadingData, store.loadDataError, children])

        // Якщо виконується loadData, рендеримо loadTemplate або fallback
        if (store.isLoadingData) {
            const TemplateComponent = LayoutTemplateConfig[routeTemplate]
            const LoadTemplateComponent = (TemplateComponent as any)?.loadTemplate

            if (LoadTemplateComponent) {
                return <LoadTemplateComponent />
            }

            // Fallback якщо loadTemplate не визначено
            return (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100vh',
                        fontSize: '18px',
                        color: '#666',
                    }}
                >
                    Loading...
                </div>
            )
        }

        // Якщо сталася помилка при завантаженні даних
        if (store.loadDataError) {
            return (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100vh',
                        fontSize: '18px',
                        color: '#ff4d4f',
                    }}
                >
                    Error loading data: {store.loadDataError.message}
                </div>
            )
        }

        // Перевіряємо, чи layoutContext створено
        if (!layoutContext) {
            return null
        }

        // Рендеримо layoutContext тільки після завантаження даних
        return (
            <LayoutContextProvider value={layoutContext}>
                {layoutContext.render()}
            </LayoutContextProvider>
        )
    }
)

LayoutLoadingState.displayName = 'LayoutLoadingState'
